/**
 * FormDictionaryContext - Diccionario Global de Datos del Formulario
 * 
 * Propósito: Compartir datos cargados una sola vez entre todas las secciones del formulario,
 * evitando requests redundantes y optimizando el autocompletado.
 * 
 * Diseñado para ser extensible a futuros módulos:
 * - EPP (actual)
 * - Residuos (actual)
 * - Combustibles (futuro)
 * - Derechos de Agua (futuro)
 * - RILes (futuro)
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const FormDictionaryContext = createContext(null);

export const FormDictionaryProvider = ({ children }) => {
	const [establishments, setEstablishments] = useState([]);
	const [warehouses, setWarehouses] = useState([]);
	const [species, setSpecies] = useState([]);
	const [cultivationCenters, setCultivationCenters] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	/**
	 * Estado de carga granular para cada tipo de maestro
	 * Derivado de los datos para evitar re-renders adicionales
	 */
	const mastersReady = useMemo(() => ({
		establishments: establishments.length > 0,
		warehouses: warehouses.length > 0,
		species: species.length > 0,
		cultivationCenters: cultivationCenters.length > 0,
		residues: false, // TODO: implementar cuando se agregue carga de residuos
		transfers: false, // TODO: implementar cuando se agregue carga de transferencias
	}), [establishments.length, warehouses.length, species.length, cultivationCenters.length]);

	/**
	 * Maps memoizados para lookups O(1) - evita búsquedas lineales O(n)
	 * Se reconstruyen solo cuando cambian los arrays de datos
	 */
	const establishmentsMap = useMemo(() => {
		const map = new Map();
		establishments.forEach(est => map.set(est._id, est));
		return map;
	}, [establishments]);

	const warehousesMap = useMemo(() => {
		const map = new Map();
		warehouses.forEach(wh => map.set(wh._id, wh));
		return map;
	}, [warehouses]);

	const speciesMap = useMemo(() => {
		const map = new Map();
		species.forEach(sp => map.set(sp._id, sp));
		return map;
	}, [species]);

	const cultivationCentersMap = useMemo(() => {
		const map = new Map();
		cultivationCenters.forEach(cc => map.set(cc._id, cc));
		return map;
	}, [cultivationCenters]);

	/**
	 * Lookup O(1) de establishment por ID usando Map
	 * Mucho más eficiente que find() lineal O(n)
	 */
	const findEstablishmentById = useCallback(
		id => {
			return establishmentsMap.get(id) || null;
		},
		[establishmentsMap]
	);

	/**
	 * Lookup O(1) de warehouse por ID usando Map
	 */
	const findWarehouseById = useCallback(
		id => {
			return warehousesMap.get(id) || null;
		},
		[warehousesMap]
	);

	/**
	 * Lookup O(1) de species por ID usando Map
	 */
	const findSpeciesById = useCallback(
		id => {
			return speciesMap.get(id) || null;
		},
		[speciesMap]
	);

	/**
	 * Lookup O(1) de cultivation center por ID usando Map
	 */
	const findCultivationCenterById = useCallback(
		id => {
			return cultivationCentersMap.get(id) || null;
		},
		[cultivationCentersMap]
	);

	/**
	 * Verifica si los maestros especificados están cargados
	 * @param {string[]} requiredMasters - Lista de maestros requeridos
	 * @returns {boolean}
	 */
	const areMastersReady = useCallback(
		requiredMasters => {
			if (!requiredMasters || requiredMasters.length === 0) return true;
			return requiredMasters.every(master => mastersReady[master] === true);
		},
		[mastersReady]
	);

	/**
	 * Filtrar warehouses por establishment_id
	 * Usa el array directamente ya que filter siempre es O(n)
	 */
	const getWarehousesByEstablishment = useCallback(
		establishmentId => {
			return warehouses.filter(wh => wh.establishment_id === establishmentId);
		},
		[warehouses]
	);

	/**
	 * Filtrar warehouses por tipo de uso (ej: EPP)
	 */
	const getWarehousesByUsageType = useCallback(
		usageTypeId => {
			return warehouses.filter(wh => wh.warehouse_usage_type_id === usageTypeId);
		},
		[warehouses]
	);

	/**
	 * Cargar datos iniciales en paralelo usando Promise.all
	 * Evita waterfalls de requests secuenciales
	 * NOTA: mastersReady se deriva automáticamente de los datos cargados
	 */
	const loadDictionaryData = useCallback(
		async (dispatch, getEstablishments, getWarehouses) => {
			setIsLoading(true);
			setError(null);

			try {
				const promises = [];

				// Cargar establecimientos
				if (getEstablishments) {
					const estPromise = dispatch(getEstablishments()).unwrap().then(data => {
						setEstablishments(data);
						return data;
					});
					promises.push(estPromise);
				}

				// Cargar warehouses
				if (getWarehouses) {
					const whPromise = dispatch(getWarehouses()).unwrap().then(data => {
						setWarehouses(data);
						return data;
					});
					promises.push(whPromise);
				}

				await Promise.all(promises);
				return { success: true };
			} catch (err) {
				setError(err);
				return { success: false, error: err };
			} finally {
				setIsLoading(false);
			}
		},
		[]
	);

	/**
	 * Actualizar establecimientos de forma optimizada
	 */
	const updateEstablishments = useCallback((newEstablishments) => {
		setEstablishments(newEstablishments);
	}, []);

	/**
	 * Actualizar bodegas de forma optimizada
	 */
	const updateWarehouses = useCallback((newWarehouses) => {
		setWarehouses(newWarehouses);
	}, []);

	/**
	 * Actualizar especies de forma optimizada
	 */
	const updateSpecies = useCallback((newSpecies) => {
		setSpecies(newSpecies);
	}, []);

	/**
	 * Actualizar centros de cultivo de forma optimizada
	 */
	const updateCultivationCenters = useCallback((newCultivationCenters) => {
		setCultivationCenters(newCultivationCenters);
	}, []);

	// MEMOIZAR el value del context para evitar re-renders innecesarios
	const value = useMemo(
		() => ({
			// State
			establishments,
			warehouses,
			species,
			cultivationCenters,
			isLoading,
			isDictionaryLoading: isLoading,
			error,
			mastersReady,

			// Actions
			setEstablishments: updateEstablishments,
			setWarehouses: updateWarehouses,
			setSpecies: updateSpecies,
			setCultivationCenters: updateCultivationCenters,
			loadDictionaryData,

			// Lookups O(1) con Maps
			establishmentsMap,
			warehousesMap,
			speciesMap,
			cultivationCentersMap,

			// Helpers memoizados
			findEstablishmentById,
			findWarehouseById,
			findSpeciesById,
			findCultivationCenterById,
			areMastersReady,
			getWarehousesByEstablishment,
			getWarehousesByUsageType,
		}),
		[
			establishments,
			warehouses,
			species,
			cultivationCenters,
			isLoading,
			error,
			mastersReady,
			updateEstablishments,
			updateWarehouses,
			updateSpecies,
			updateCultivationCenters,
			loadDictionaryData,
			establishmentsMap,
			warehousesMap,
			speciesMap,
			cultivationCentersMap,
			findEstablishmentById,
			findWarehouseById,
			findSpeciesById,
			findCultivationCenterById,
			areMastersReady,
			getWarehousesByEstablishment,
			getWarehousesByUsageType,
		]
	);

	return (
		<FormDictionaryContext.Provider value={value}>
			{children}
		</FormDictionaryContext.Provider>
	);
};

/**
 * Hook para acceder al diccionario de datos del formulario
 */
export const useFormDictionary = () => {
	const context = useContext(FormDictionaryContext);
	if (!context) {
		throw new Error(
			'useFormDictionary must be used within FormDictionaryProvider'
		);
	}
	return context;
};
